import { isProductionEnvironment, isStagingEnvironment } from './env';

/**
 * Sentry configuration helpers for environment-specific settings
 */

/**
 * Get the traces sample rate based on current environment
 * @returns Sample rate between 0.0 and 1.0
 */
export function getTracesSampleRate(): number {
  if (process.env.NODE_ENV === 'development') {
    return 1.0; // 100% sampling in local development for full visibility
  }

  if (isProductionEnvironment()) {
    return 0.1; // 10% sampling in production for scale
  }

  if (isStagingEnvironment()) {
    return 0.5; // 50% sampling in staging/QA for cost/coverage balance
  }

  return 0.0; // No sampling for unknown environments
}

/**
 * Get the session replay sample rate based on current environment
 * @returns Sample rate between 0.0 and 1.0
 */
export function getReplaySessionSampleRate(): number {
  if (process.env.NODE_ENV === 'development') {
    return 0.0; // Opt-in only in local development (can be distracting during dev)
  }

  if (isProductionEnvironment()) {
    return 0.01; // 1% session replay in production (cost-effective)
  }

  if (isStagingEnvironment()) {
    return 0.1; // 10% session replay in staging/QA
  }

  return 0.0;
}

/**
 * Get trace propagation targets for distributed tracing
 * Only includes our own API endpoints to avoid CORS issues with third-party services
 * Excludes: Auth0, ThoughtSpot, WalkMe, and other external services
 * @returns Array of string patterns or RegExp for matching URLs
 */
export function getTracePropagationTargets(): (string | RegExp)[] {
  const targets: (string | RegExp)[] = [
    // Relative URLs for same-origin API calls (matches Sentry's default behavior)
    /^\/(?!\/)/,
  ];

  // Local development - only localhost API calls
  if (process.env.NODE_ENV === 'development') {
    targets.push(/^https?:\/\/localhost:\d+/);
  }

  // Staging/QA environments
  if (isStagingEnvironment()) {
    // Excludes auth.stage*.nonprod.alchemer.com
    targets.push(/^https?:\/\/(?!auth\.)(.*\.)?stage[2-9]\.nonprod\.alchemer\.com/);
  }

  // Production
  if (isProductionEnvironment()) {
    // Excludes auth.alchemer.com subdomains
    targets.push(/^https:\/\/(?!auth\.)(.*\.)?alchemer\.com/);
  }

  return targets;
}

/**
 * Get list of errors to ignore in Sentry
 * @returns Array of error messages or patterns to ignore
 */
export function getIgnoreErrors(): (string | RegExp)[] {
  return [
    // Browser quirks
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    'Non-Error promise rejection captured',

    // Auth0 expected errors
    'login_required',
    'consent_required',

    // Network errors we can't control
    'Failed to fetch',
    'NetworkError',
    'Network request failed',
    'CORS error occurred',
  ];
}

/**
 * Get list of URLs to deny (e.g., browser extensions)
 * @returns Array of RegExp patterns for URLs to deny
 */
export function getDenyUrls(): RegExp[] {
  return [
    // Browser extensions
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-extension:\/\//i,
  ];
}

/**
 * Determine if we should create a Sentry span for the given request URL
 * Excludes third-party services we don't control (Auth0, ThoughtSpot, WalkMe)
 * @param url - The request URL to check
 * @returns true if we should create a span, false otherwise
 */
export function shouldCreateSpanForRequest(url: string): boolean {
  // Exclude third-party services
  const excludedDomains = [
    'auth.al2.testgizmo.us', // Auth0
    'thoughtspot.cloud', // ThoughtSpot
    'thoughtspot.eu', // ThoughtSpot EU
    'thoughtspot.ca', // ThoughtSpot CA
    'walkme.com', // WalkMe
    'cdn.walkme.com', // WalkMe CDN
    'ec.walkme.com', // WalkMe Events
    'papi.walkme.com', // WalkMe API
  ];

  return !excludedDomains.some((domain) => url.includes(domain));
}
